import React, { useState, useContext } from "react";
import { VoteOnListItem } from "../api-requests/Votes";
import { getBearerHeaders } from "../helpers/GetBearerHeaders";
import { UserContext } from "../context/UserContext";
import { ListItemVoteType, ListItemVoteTotals } from "../types/votes";
import { calculatePlacementVotePercentages } from "../helpers/VoteCalculations";

interface ListItemVoteBarProps {
  listId: string;
  listItemId: string;
  placementVoteTotals: ListItemVoteTotals;
  currentUserPlacementVote: ListItemVoteType | null;
  rank: number;
  totalItems: number;
  onVoteUpdate: (
    totals: ListItemVoteTotals,
    userVote: ListItemVoteType,
  ) => void;
}

const ListItemVoteBar: React.FC<ListItemVoteBarProps> = ({
  listId,
  listItemId,
  placementVoteTotals,
  currentUserPlacementVote,
  rank,
  totalItems,
  onVoteUpdate,
}) => {
  const { user } = useContext(UserContext);
  const [isLoading, setIsLoading] = useState<string | null>(null);

  const handleVote = async (voteType: ListItemVoteType) => {
    if (!user) {
      // TODO: Show login prompt using existing auth pattern
      alert("Please log in to vote");
      return;
    }

    if (isLoading) return;

    // Prevent voting on disabled buttons
    if (voteType === "too_high" && rank === totalItems) return;
    if (voteType === "too_low" && rank === 1) return;

    setIsLoading(voteType);
    try {
      const headers = getBearerHeaders();
      const response = await VoteOnListItem(
        listId,
        listItemId,
        voteType,
        headers,
      );
      onVoteUpdate(response.placementVoteTotals, voteType);
    } catch (error) {
      console.error("Error voting on list item:", error);
      // TODO: Show error message using existing error pattern
    } finally {
      setIsLoading(null);
    }
  };

  const totalVotes =
    placementVoteTotals.tooHigh +
    placementVoteTotals.rightSpot +
    placementVoteTotals.tooLow;

  const percentages = calculatePlacementVotePercentages(placementVoteTotals);

  // Determine which buttons should be disabled based on rank
  const isTooHighDisabled = rank === totalItems; // Can't say "too high" for last place
  const isTooLowDisabled = rank === 1; // Can't say "too low" for first place

  return (
    <div className="">
      <div className=" text-yellow-200 text-xs font-bold 0 mb-2">
        How does this placement feel?
      </div>

      <div className="flex gap-2">
        <button
          onClick={() => handleVote("too_high")}
          disabled={isLoading !== null || isTooHighDisabled}
          className={`btn-rank ${
            currentUserPlacementVote === "too_high"
              ? "bg-red-600 text-white border border-red-400"
              : isTooHighDisabled
                ? "bg-gray-800 text-gray-500 border border-gray-600 cursor-not-allowed"
                : "bg-gray-700 text-gray-300 hover:bg-red-700 border border-transparent hover:border-red-400"
          } ${isLoading === "too_high" ? "opacity-50 cursor-not-allowed" : ""}`}
        >
          {isLoading === "too_high"
            ? "..."
            : `☝️ Too High ${totalVotes > 0 ? `(${percentages.tooHighPct}%)` : ""}`}
        </button>

        <button
          onClick={() => handleVote("right_spot")}
          disabled={isLoading !== null}
          className={`btn-rank ${
            currentUserPlacementVote === "right_spot"
              ? "bg-green-600 text-white border border-green-400"
              : "bg-gray-700 text-gray-300 hover:bg-green-700 border border-transparent hover:border-green-400"
          } ${isLoading === "right_spot" ? "opacity-50 cursor-not-allowed" : ""}`}
        >
          {isLoading === "right_spot"
            ? "..."
            : `💯 Right Spot ${totalVotes > 0 ? `(${percentages.rightSpotPct}%)` : ""}`}
        </button>

        <button
          onClick={() => handleVote("too_low")}
          disabled={isLoading !== null || isTooLowDisabled}
          className={`btn-rank ${
            currentUserPlacementVote === "too_low"
              ? "bg-blue-600 text-white border border-blue-400"
              : isTooLowDisabled
                ? "bg-gray-800 text-gray-500 border border-gray-600 cursor-not-allowed"
                : "bg-gray-700 text-gray-300 hover:bg-blue-700 border border-transparent hover:border-blue-400"
          } ${isLoading === "too_low" ? "opacity-50 cursor-not-allowed" : ""}`}
        >
          {isLoading === "too_low"
            ? "..."
            : `👇 Too Low ${totalVotes > 0 ? `(${percentages.tooLowPct}%)` : ""}`}
        </button>
      </div>
    </div>
  );
};

export default ListItemVoteBar;
